import {ChatInputValidationError} from '@github-ui/copilot-chat-state/validation-error'
import {sendEvent, type SendEventContext} from '@github-ui/hydro-analytics'

import {
  COMPOSER_EVENTS,
  COMPOSER_TELEMETRY_SCHEMA_VERSION,
  LENGTH_BUCKETS,
  type ComposerAbandonTrigger,
  type ComposerAction,
  type ComposerAttachmentOrigin,
  type ComposerAttachmentType,
  type ComposerAttachmentVia,
  type ComposerContextType,
  type ComposerPasteKind,
  type ComposerSubmitBlockedReason,
  type ComposerSubmitMethod,
  type ComposerSubmitTarget,
  type ComposerSurface,
  type LengthBucket,
} from './constants'

export function bucketLength(length: number): LengthBucket {
  const safe = Number.isFinite(length) && length > 0 ? length : 0
  for (const bucket of LENGTH_BUCKETS) {
    if (safe <= bucket.max) return bucket.label
  }
  return '2001+'
}

export interface ComposerTelemetryConfig {
  surface: ComposerSurface
  contextType?: ComposerContextType
  /** The existing direct-turn latency surface, forwarded unchanged when the host already has one. */
  directTurnSurface?: string
}

export interface TelemetryAttachment {
  id: string
  type: ComposerAttachmentType
  origin: ComposerAttachmentOrigin
}

export type ComposerEventFields = Record<string, string | number | boolean | undefined>

export interface ComposerSubmittedDetails {
  promptLength: number
  attachmentsCount?: number
  modelId?: string
  /** Request id shared with the existing direct-turn latency telemetry; joins this session to the turn. */
  directTurnRequestId?: string
}

interface TrackerDeps {
  send?: (type: string, context: SendEventContext) => void
  /** Runs queued sends later. Defaults to idle time, or synchronously when `send` is injected. */
  schedule?: (flush: () => void) => void
  now?: () => number
  createId?: () => string
}

interface SessionSummary {
  interacted: boolean
  hadUserFocus: boolean
  typed: boolean
  lastLength: number
  editCount: number
  clearCount: number
  pasteCount: number
  maxPasteLength: number
  modelChanged: boolean
  reasoningEffortChanged: boolean
  scopeChanged: boolean
  agentChanged: boolean
  modeChanged: boolean
  approvalModeChanged: boolean
  repositoryChanged: boolean
  branchChanged: boolean
  environmentChanged: boolean
  attachmentsAdded: number
  attachmentsRemoved: number
  attachmentTypes: Set<ComposerAttachmentType>
  usedSuggestion: boolean
  spaceSelected: boolean
  finalModelId: string | undefined
}

function emptySummary(): SessionSummary {
  return {
    interacted: false,
    hadUserFocus: false,
    typed: false,
    lastLength: 0,
    editCount: 0,
    clearCount: 0,
    pasteCount: 0,
    maxPasteLength: 0,
    modelChanged: false,
    reasoningEffortChanged: false,
    scopeChanged: false,
    agentChanged: false,
    modeChanged: false,
    approvalModeChanged: false,
    repositoryChanged: false,
    branchChanged: false,
    environmentChanged: false,
    attachmentsAdded: 0,
    attachmentsRemoved: 0,
    attachmentTypes: new Set(),
    usedSuggestion: false,
    spaceSelected: false,
    finalModelId: undefined,
  }
}

const SUMMARY_FLAG_BY_ACTION: Partial<Record<ComposerAction, keyof SessionSummary>> = {
  model_changed: 'modelChanged',
  reasoning_effort_changed: 'reasoningEffortChanged',
  scope_changed: 'scopeChanged',
  agent_changed: 'agentChanged',
  mode_changed: 'modeChanged',
  approval_mode_changed: 'approvalModeChanged',
  repository_changed: 'repositoryChanged',
  branch_changed: 'branchChanged',
  environment_changed: 'environmentChanged',
  suggestion_selected: 'usedSuggestion',
  space_selected: 'spaceSelected',
}

type ComposerEditSource = 'typing' | 'paste' | 'upload'
const EDIT_SOURCE_TTL_MS = 1000

function defaultCreateId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  // randomUUID needs a secure context; getRandomValues does not.
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join('')
}

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

/**
 * Collects one composer session — shown, interactions, then submit or
 * abandonment — and emits it to Hydro. It holds every "once" guard itself so
 * React re-renders, Strict Mode double effects, and repeated callbacks cannot
 * produce duplicate events. Never pass it prompt text: only lengths and enums.
 */
export class ComposerTelemetryTracker {
  private config: ComposerTelemetryConfig
  private readonly send: (type: string, context: SendEventContext) => void
  private readonly schedule: (flush: () => void) => void
  private pendingSends: Array<[string, SendEventContext]> = []
  private flushScheduled = false
  private readonly now: () => number
  private readonly createId: () => string

  private sessionIdValue: string
  private startedAt: number
  private shownSent = false
  private focusSent = false
  private firstInputSent = false
  private ended = false
  private endedAt = 0
  private summary = emptySummary()
  private attachments: Map<string, TelemetryAttachment> | null = null
  private pendingSubmitMethod: ComposerSubmitMethod | undefined
  private pendingAttachmentVia: ComposerAttachmentVia | undefined
  private pendingDispatch: {target: ComposerSubmitTarget; directTurnRequestId?: string} | undefined
  private pendingSubmitModelId: string | undefined
  private submitAttachmentsCount: number | undefined
  private pendingEditSource: {source: ComposerEditSource; at: number} | undefined
  private submitState: 'idle' | 'in_flight' | 'blocked' = 'idle'

  constructor(config: ComposerTelemetryConfig, deps: TrackerDeps = {}) {
    this.config = config
    // content_free reports a fixed page and no URL, title, or referrer, which can name private repositories or files.
    this.send = deps.send ?? ((type, context) => sendEvent(type, context, {privacy: 'content_free'}))
    this.schedule = deps.schedule ?? (deps.send ? runNow : scheduleWhenIdle)
    this.now = deps.now ?? defaultNow
    this.createId = deps.createId ?? defaultCreateId
    this.sessionIdValue = this.createId()
    this.startedAt = this.now()
  }

  get sessionId(): string {
    return this.sessionIdValue
  }

  updateConfig(config: ComposerTelemetryConfig) {
    this.config = config
  }

  /** Marks the composer visible. Emits at most once per composer session. */
  shown(fields: {hasInitialPrompt?: boolean; isNewConversation?: boolean} = {}) {
    this.resumeIfEnded()
    if (this.shownSent) return
    this.shownSent = true
    this.emitInteraction('shown', {
      shown_reason: 'mount',
      ...(fields.hasInitialPrompt !== undefined ? {has_initial_prompt: fields.hasInitialPrompt} : {}),
      ...(fields.isNewConversation !== undefined ? {is_new_conversation: fields.isNewConversation} : {}),
    })
  }

  /** First focus only. Autofocus on mount is reported but does not count as engagement. */
  focused(isAutofocus: boolean) {
    this.resumeIfEnded()
    if (!isAutofocus) {
      this.summary.hadUserFocus = true
      this.summary.interacted = true
    }
    if (this.focusSent) return
    this.focusSent = true
    this.emitInteraction('focused', {is_autofocus: isAutofocus})
  }

  /** Records text the user did not type (a restored draft, a clear after submit) without counting it as an edit. */
  textBaseline(length: number) {
    this.summary.lastLength = length
  }

  /**
   * Marks the next text change as coming from a paste or an inline image
   * upload rather than typing. The hint expires quickly so a paste that
   * changes nothing cannot mislabel a later keystroke.
   */
  noteEditSource(source: ComposerEditSource) {
    this.pendingEditSource = {source, at: this.now()}
  }

  /**
   * Called for user-driven text changes only, with the new text length. A
   * paste or an inline image upload updates the length and counts as
   * interaction, but is not typing.
   */
  textChanged(length: number) {
    const hint = this.pendingEditSource
    this.pendingEditSource = undefined
    const source = hint && this.now() - hint.at <= EDIT_SOURCE_TTL_MS ? hint.source : 'typing'
    this.resumeIfEnded()
    const previous = this.summary.lastLength
    this.summary.lastLength = length
    this.summary.interacted = true
    if (source !== 'typing') return
    this.summary.editCount += 1
    if (length > 0) {
      this.summary.typed = true
      if (!this.firstInputSent) {
        this.firstInputSent = true
        this.emitInteraction('first_input', {ms_to_first_input: this.elapsed()})
      }
    } else if (previous > 0) {
      this.summary.clearCount += 1
      this.emitInteraction('cleared')
    }
  }

  paste(kind: ComposerPasteKind, length: number) {
    this.resumeIfEnded()
    this.summary.interacted = true
    this.summary.pasteCount += 1
    this.summary.maxPasteLength = Math.max(this.summary.maxPasteLength, length)
    if (kind === 'files') this.pendingAttachmentVia = 'paste_or_drop'
    else this.noteEditSource('paste')
    this.emitInteraction('paste', {
      paste_kind: kind,
      paste_length_bucket: bucketLength(length),
      paste_index: this.summary.pasteCount,
    })
  }

  /** Records a discrete user action and folds it into the session summary. */
  track(action: ComposerAction, fields: ComposerEventFields = {}) {
    this.resumeIfEnded()
    this.summary.interacted = true
    const flag = SUMMARY_FLAG_BY_ACTION[action]
    if (flag) (this.summary[flag] as boolean) = true
    if (action === 'model_changed' && typeof fields.to_model_id === 'string') {
      this.summary.finalModelId = fields.to_model_id
    }
    if (action === 'space_cleared') this.summary.spaceSelected = false
    this.emitInteraction(action, fields)
  }

  /** Hint for the next user attachment(s): which control added them. */
  noteAttachmentVia(via: ComposerAttachmentVia) {
    this.pendingAttachmentVia = via
  }

  /**
   * Diffs the composer's attachment list. The first list is the baseline
   * (page context the composer opened with) and is not reported as additions.
   */
  attachmentsChanged(next: readonly TelemetryAttachment[]) {
    // Spaces report through space_selected / space_cleared instead.
    const nextById = new Map(
      next.filter(attachment => attachment.type !== 'space').map(attachment => [attachment.id, attachment]),
    )
    const previous = this.attachments
    this.attachments = nextById
    // A submit clears the prompt and its attachments, during or right after the submit; that is not the user removing them.
    if (previous === null || this.submitState === 'in_flight') return
    const via = this.pendingAttachmentVia
    let addedUser = false
    for (const [id, attachment] of nextById) {
      if (previous.has(id)) continue
      if (this.ended && attachment.origin !== 'user') continue
      this.resumeIfEnded()
      if (attachment.origin === 'user') {
        this.summary.interacted = true
        this.summary.attachmentsAdded += 1
        addedUser = true
      }
      this.summary.attachmentTypes.add(attachment.type)
      this.emitInteraction('attachment_added', {
        attachment_type: attachment.type,
        attachment_origin: attachment.origin,
        attachment_via: attachment.origin === 'user' ? (via ?? 'unknown') : undefined,
      })
    }
    for (const [id, attachment] of previous) {
      if (nextById.has(id) || this.ended) continue
      this.summary.interacted = true
      this.summary.attachmentsRemoved += 1
      this.emitInteraction('attachment_removed', {
        attachment_type: attachment.type,
        attachment_origin: attachment.origin,
      })
    }
    if (addedUser) this.pendingAttachmentVia = undefined
  }

  /** The model currently shown in the picker. Not an interaction; used as the session's final model. */
  noteModel(modelId: string | undefined) {
    if (modelId) this.summary.finalModelId = modelId
  }

  /** The model the submit is actually sent with, which can differ from the picker (for example a prepared prompt). */
  noteSubmitModel(modelId: string | undefined) {
    this.pendingSubmitModelId = modelId
  }

  /** Remembers how the user triggered the next submit. Consumed by {@link submitted}. */
  noteSubmitMethod(method: ComposerSubmitMethod) {
    this.pendingSubmitMethod = method
  }

  /**
   * Marks a submit as started. A submit often navigates away and unmounts the
   * composer before it resolves, so an in-flight submit suppresses abandonment.
   */
  submitStarted() {
    this.resumeIfEnded()
    this.submitState = 'in_flight'
    this.submitAttachmentsCount = this.attachments?.size
  }

  /**
   * The submit passed the composer's readiness checks and is being sent.
   * `directTurnRequestId` is the id the existing direct-turn latency telemetry uses, when it applies.
   */
  noteSubmitDispatch(dispatch: {target: ComposerSubmitTarget; directTurnRequestId?: string}) {
    this.pendingDispatch = dispatch
  }

  /** The submit did not go through. The session continues; a later {@link submitted} for this attempt is ignored. */
  submitBlocked(reason: ComposerSubmitBlockedReason) {
    if (this.submitState === 'blocked') return
    this.submitState = 'blocked'
    this.pendingSubmitMethod = undefined
    this.pendingDispatch = undefined
    this.pendingSubmitModelId = undefined
    this.track('submit_blocked', {submit_blocked_reason: reason})
  }

  /** Ends the session as submitted. Safe to call more than once; only the first call per session emits. */
  submitted(details: ComposerSubmittedDetails) {
    const wasBlocked = this.submitState === 'blocked'
    this.submitState = 'idle'
    if (this.ended || wasBlocked) return
    const method = this.pendingSubmitMethod ?? 'unknown'
    this.pendingSubmitMethod = undefined
    const finalModelId = details.modelId ?? this.pendingSubmitModelId ?? this.summary.finalModelId
    this.emitSessionEnd('submitted', {
      submit_method: method,
      ms_to_submit: this.elapsed(),
      prompt_length_bucket: bucketLength(details.promptLength),
      attachments_count: details.attachmentsCount ?? this.submitAttachmentsCount ?? this.attachments?.size ?? 0,
      final_model_id: finalModelId,
      submit_target: this.pendingDispatch?.target,
      direct_turn_request_id: details.directTurnRequestId ?? this.pendingDispatch?.directTurnRequestId,
    })
    this.pendingDispatch = undefined
    this.pendingSubmitModelId = undefined
  }

  /** Ends the session as abandoned when the user engaged but never submitted. */
  abandon(trigger: ComposerAbandonTrigger) {
    if (this.ended || this.submitState === 'in_flight' || !this.summary.interacted) return
    this.emitSessionEnd('abandoned', {
      abandon_trigger: trigger,
      prompt_length_bucket: bucketLength(this.summary.lastLength),
      attachments_count: this.attachments?.size ?? 0,
      final_model_id: this.summary.finalModelId,
    })
  }

  private emitSessionEnd(outcome: 'submitted' | 'abandoned', fields: ComposerEventFields) {
    const s = this.summary
    this.emit(COMPOSER_EVENTS.sessionEnd, {
      outcome,
      typed: s.typed,
      had_user_focus: s.hadUserFocus,
      edit_count: s.editCount,
      clear_count: s.clearCount,
      paste_used: s.pasteCount > 0,
      paste_count: s.pasteCount,
      max_paste_length_bucket: bucketLength(s.maxPasteLength),
      model_changed: s.modelChanged,
      reasoning_effort_changed: s.reasoningEffortChanged,
      scope_changed: s.scopeChanged,
      agent_changed: s.agentChanged,
      mode_changed: s.modeChanged,
      approval_mode_changed: s.approvalModeChanged,
      repository_changed: s.repositoryChanged,
      branch_changed: s.branchChanged,
      environment_changed: s.environmentChanged,
      attachments_added_count: s.attachmentsAdded,
      attachments_removed_count: s.attachmentsRemoved,
      attachment_types: [...s.attachmentTypes].sort().join(','),
      used_suggestion: s.usedSuggestion,
      space_selected: s.spaceSelected,
      ...fields,
    })
    this.ended = true
    this.endedAt = this.now()
  }

  /**
   * A composer that stays mounted after a submit starts a new session on the
   * next activity, timed from the submit, so each prompt gets its own funnel.
   */
  private resumeIfEnded() {
    if (!this.ended) return
    this.ended = false
    this.sessionIdValue = this.createId()
    this.startedAt = this.endedAt
    // The picker keeps its model across prompts, so the next session starts from it.
    const {finalModelId} = this.summary
    this.summary = {...emptySummary(), finalModelId}
    this.firstInputSent = false
    this.focusSent = true
    this.shownSent = true
    this.pendingSubmitMethod = undefined
    this.pendingDispatch = undefined
    this.pendingSubmitModelId = undefined
    this.emitInteraction('shown', {shown_reason: 'after_submit'})
  }

  private elapsed(): number {
    return Math.max(0, Math.round(this.now() - this.startedAt))
  }

  private emitInteraction(action: ComposerAction, fields: ComposerEventFields = {}) {
    this.emit(COMPOSER_EVENTS.interaction, {action, ...fields})
  }

  private emit(type: string, fields: ComposerEventFields) {
    const context: SendEventContext = {
      schema_version: COMPOSER_TELEMETRY_SCHEMA_VERSION,
      composer_session_id: this.sessionIdValue,
      composer_surface: this.config.surface,
      context_type: this.config.contextType ?? 'none',
      ms_since_shown: this.elapsed(),
    }
    if (this.config.directTurnSurface) context.direct_turn_surface = this.config.directTurnSurface
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) context[key] = value
    }
    // Sending is deferred so no telemetry work runs inside a keystroke, click, or submit handler.
    this.pendingSends.push([type, context])
    if (this.flushScheduled) return
    this.flushScheduled = true
    this.schedule(() => this.flushPending())
  }

  /** Sends queued events now. Called from idle time, and when the page hides so nothing queued is lost. */
  flushPending() {
    this.flushScheduled = false
    const sends = this.pendingSends
    this.pendingSends = []
    for (const [type, context] of sends) {
      try {
        this.send(type, context)
      } catch {
        // Telemetry must never break composing.
      }
    }
  }
}

function runNow(flush: () => void) {
  flush()
}

function scheduleWhenIdle(flush: () => void) {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(flush, {timeout: 2000})
  else setTimeout(flush, 0)
}

/**
 * Runs a composer submit and reports its outcome: `submitted` when it goes
 * through, `submit_blocked` when it returns `false` or throws. Errors are
 * re-thrown unchanged.
 */
export async function instrumentSubmit<T>(
  tracker: ComposerTelemetryTracker | null,
  method: ComposerSubmitMethod,
  promptLength: number,
  submit: () => T | Promise<T>,
): Promise<T> {
  if (!tracker) return submit()
  guard(() => {
    tracker.submitStarted()
    tracker.noteSubmitMethod(method)
  })
  let result: T
  try {
    result = await submit()
  } catch (error) {
    guard(() => tracker.submitBlocked(error instanceof ChatInputValidationError ? 'validation' : 'error'))
    throw error
  }
  guard(() => {
    if (result === false) tracker.submitBlocked('rejected')
    else tracker.submitted({promptLength})
  })
  return result
}

function guard(report: () => void) {
  try {
    report()
  } catch {
    // Telemetry must never change the submit outcome.
  }
}
